'use client';

import TrySeozastCTA from '@/app/components/TrySeozastCTA';
import Head from 'next/head';
import Image from 'next/image';

const Page = () => {
  const canonicalUrl = 'https://seozast.com/articles/get-found-in-chatgpt-and-perplexity';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Head con canonical */}
      <Head>
        <title>AI SEO in 2025: How to Make Your Content AI-Citation Ready</title>
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      <section className="mb-16">
        <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4 tracking-tight">
          How to Get Your Site Found by ChatGPT and Perplexity in 2025
        </h1>
        <div className="text-sm text-neutral-500 mb-8 flex items-center space-x-3">
          <time dateTime="2025-07-04">July 4, 2025</time>
          <span className="text-neutral-300">•</span>
          <span>By SEOzast Team</span>
          <span className="text-neutral-300">•</span>
          <span>6 min read</span>
        </div>

        <div className="relative w-full h-[300px] md:h-[450px] mb-10 rounded-xl overflow-hidden shadow-xl">
          <Image
            src="https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"
            alt="Found by AI"
            fill
            className="object-cover"
            priority
          />
          <figcaption className="absolute bottom-2 right-2 text-xs text-white bg-black/50 px-2 py-1 rounded">
            Photo by Tara Winstead / Pexels
          </figcaption>
        </div>

        <div className="bg-gradient-to-r from-neutral-100 to-neutral-200 p-8 rounded-xl mb-12 shadow-md border border-neutral-200">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">Quick Summary</h2>
          <p className="text-neutral-700 leading-relaxed">
            Getting your content cited by tools like ChatGPT, Perplexity AI, and Google SGE is the new frontier of visibility in 2025. This is called <strong>GEO (Generative Engine Optimization)</strong>. In this guide, you'll learn practical steps to make your site appear in AI-generated answers — and how <a href="https://seozast.com" className="text-emerald-600 hover:underline font-medium">SEOzast</a> can help you do it faster and cheaper than ever.
          </p>
        </div>

        <div className="article-content space-y-10 text-neutral-800 leading-relaxed font-sans">
          <h2 className="text-2xl font-semibold text-neutral-900">Why GEO Matters More Than Ever</h2>
          <p>
            Traditional SEO focuses on search rankings. But AI platforms like <strong>ChatGPT (with browsing)</strong>, <strong>Perplexity AI</strong>, and <strong>Google's Search Generative Experience</strong> generate answers and cite sources. If you're not cited, you're invisible.
          </p>
          <p>
            According to <a href="https://searchengineland.com/google-ai-overview-impact-organic-traffic-433481" target="_blank" className="text-emerald-600 hover:underline">Search Engine Land</a>, Google SGE has reduced organic traffic by up to 30%. Meanwhile, Perplexity AI and ChatGPT are gaining millions of users monthly.
          </p>

          <h2 className="text-2xl font-semibold text-neutral-900">How Do These AI Tools Choose What to Cite?</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Fresh, crawlable content</strong> indexed via sitemaps and protocols like <a href="https://www.bing.com/indexnow" target="_blank" className="text-emerald-600 hover:underline">IndexNow</a>.</li>
            <li><strong>Clear structure</strong> with headers, lists, and tables (AI prefers clarity).</li>
            <li><strong>Factual tone</strong> with concise answers and external citations.</li>
            <li><strong>Unique perspectives</strong> or actionable summaries.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-neutral-900">6 Steps to Get Your Content Cited</h2>
          <ol className="list-decimal pl-6 space-y-4">
            <li>
              <strong>Use Clear H2/H3 Structure</strong>: Break your article into logical questions and answers.
            </li>
            <li>
              <strong>Add Summary Boxes</strong>: Use “At a Glance” or TL;DR sections at the top.
            </li>
            <li>
              <strong>Implement IndexNow</strong>: Push your new URLs directly to Bing/Yandex (soon Google).
            </li>
            <li>
              <strong>Add FAQ Schema</strong>: Helps AI understand your content as Q&A.
            </li>
            <li>
              <strong>Include Authoritative Citations</strong>: Link to reliable external sources (Moz, Semrush, gov, .edu).
            </li>
            <li>
              <strong>Answer Specific Questions</strong>: e.g., “What’s the cheapest SEO tool for 2025?” → “SEOzast is $19.99/month.”
            </li>
          </ol>

          <h2 className="text-2xl font-semibold text-neutral-900">Real Example: From Invisible to Cited</h2>
          <p>
            One SEOzast user created an article titled “Best Budget SEO Tools Under $10”. Within 2 weeks of GEO formatting + IndexNow ping, it was cited in Perplexity AI when asking “What are the cheapest SEO automation tools in 2025?”.
          </p>

          <h2 className="text-2xl font-semibold text-neutral-900">What Not to Do</h2>
          <ul className="list-disc pl-6 space-y-2 text-neutral-700">
            <li>❌ Walls of text without structure</li>
            <li>❌ Zero citations or external links</li>
            <li>❌ Outdated data or generic content</li>
            <li>❌ No sitemap.xml or IndexNow usage</li>
          </ul>

          <div className="bg-neutral-100 p-6 rounded-lg shadow-sm text-center mt-8">
            <h3 className="text-lg font-semibold mb-4">Ready to Be Cited by ChatGPT?</h3>
            <TrySeozastCTA />
          </div>

          <h2 className="text-2xl font-semibold text-neutral-900">SEOzast: GEO Optimization Without the Overhead</h2>
          <p>
            With <a href="https://seozast.com" className="text-emerald-600 hover:underline">SEOzast</a>, you get:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Instant GEO+SEO optimized articles</li>
            <li>Smart AI-assisted outlines for clarity</li>
            <li>Built-in IndexNow pings</li>
            <li>Export to Markdown, Word, or Next.js</li>
          </ul>

          <h2 className="text-2xl font-semibold text-neutral-900">FAQ: Getting Cited in AI Search</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">Can I force ChatGPT to cite me?</h3>
              <p>No, but you can make your content highly cite-worthy with GEO best practices.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Does Perplexity show backlinks?</h3>
              <p>Yes — it often cites and links to the original article when well-structured.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Is GEO replacing SEO?</h3>
              <p>Not yet. GEO is a complement, not a replacement — but it's increasingly important.</p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-neutral-900">Conclusion</h2>
          <p>
            AI tools are becoming the new search engines. If your site isn’t showing up in their responses, you’re missing traffic. With <a href="https://seozast.com" className="text-emerald-600 hover:underline">SEOzast</a>, you can create content that’s optimized for both human searchers and AI assistants — in one click.
          </p>

          <div className="bg-neutral-100 p-8 rounded-lg shadow-sm text-center">
            <h3 className="text-lg font-semibold mb-4">Be the Source AI Quotes</h3>
            <TrySeozastCTA />
          </div>

          <div className="mt-16">
            <h2 className="text-2xl font-semibold mb-6 text-neutral-900">Related Articles</h2>
            <ul className="space-y-4 text-neutral-700 list-disc pl-6">
              <li>
                <a
                  href="/articles/how-to-submit-urls-to-bing-with-indexnow"
                  className="text-emerald-600 hover:underline font-medium"
                >
                  How to Submit URLs to Bing with IndexNow (and Why It Matters)
                </a>
              </li>
              <li>
                <a
                  href="/articles/what-is-indexnow-and-how-it-helps-content-show-up-in-chatgpt"
                  className="text-emerald-600 hover:underline font-medium"
                >
                  What is IndexNow and How It Helps Your Content Show Up in ChatGPT
                </a>
              </li>
              <li>
                <a
                  href="/articles/geo-vs-seo"
                  className="text-emerald-600 hover:underline font-medium"
                >
                  GEO vs. SEO: Mastering Search in the AI Era of 2025
                </a>
              </li>
            </ul>
          </div>

          {/* FAQ Schema */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Can I force ChatGPT to cite me?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "No, but you can make your content highly cite-worthy with GEO best practices."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does Perplexity show backlinks?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes — it often cites and links to the original article when well-structured."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is GEO replacing SEO?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Not yet. GEO is a complement, not a replacement — but it's increasingly important."
                  }
                }
              ]
            })}
          </script>
        </div>
      </section>
    </div>
  );
};

export default Page;
